import React from 'react';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router';
import { uniq } from 'lodash';

// Redux
import {
  pushToSelectedRiskObjects,
  pushToSelectedCompanies,
  pushToSelectedGuarantees,
  persistSelectionState,
} from '../store/redux/slices/selectionSlice';

// Services
import { isObjectEmpty } from '../../../common/src/legacy/services/objectService';
import IntroContainer from '../components/intro/IntroContainer';
import { mq } from '../../../common/src/utils/styledComponentsUtils';
import { parse, stringify } from 'query-string';
import { getProFlowRouteNaming } from '../utils/stringUtils';
import { useLouiseDispatch, useLouiseSelector } from '../store/redux/store';
import { getFlowSessionById } from '../services/louiseApiService';
import useSalesConversationFlowPayload from '../hooks/useSalesConversationFlowPayload';
import { flowIdSalesConversation } from '../../../flows/src/constants/flowIds';
import useUuid from '../../../common/src/hooks/useUuid';
import useCampaignsOutputUrl from '../hooks/useCampaignsOutputUrl';
import useAuth from '../../../app/src/features/authentication/hooks/useAuth';
import useStartDeMotoVerzekeringFlow from '../hooks/useStartDeMotoVerzekeringFlow';
import useIsDeMotoVerzekeringEnabled from '../hooks/useIsDeMotoVerzekeringEnabled';
import useIsRiskAnalysisFlow from '../../../conversations/src/hooks/useIsRiskAnalysisFlow';
import useStartRiskAnalysisFlow from '../../../risk-analysis/src/hooks/useStartRiskAnalysisFlow';
import useIsVillasureCampaign from '../hooks/useIsVillasureCampaign';
import useStartVillasureOfferFlow from '../hooks/villasure/useStartVillasureOfferFlow';
import useFlowsInitNew from '../../../flows/src/hooks/init/useFlowsInitNew';
import useCaptchaV3 from '../../../app/src/features/authentication/hooks/useCaptchaV3';
import { isSocialLoginUrl } from '../../../app/src/features/authentication/constants/SocialAuth';

interface Props {
  nextUrl?: string;
  nextUrlCampaign?: string;
}

/**
 * This view handles everything before the questions can be received
 */
const IntroView: React.FC<React.PropsWithChildren<Props>> = ({
  nextUrlCampaign,
  nextUrl = '#',
}) => {
  const isDeMotoVerzekeringEnabled = useIsDeMotoVerzekeringEnabled();
  const isVillasureCampaign = useIsVillasureCampaign();
  const startDeMotoVerzekeringFlow = useStartDeMotoVerzekeringFlow();
  const startVillasureOfferFlow = useStartVillasureOfferFlow();
  const { distribution, campaign } = useAuth();
  const prefilledSessionId = useLouiseSelector(
    (state) => state.selection.prefilledSessionId,
  );
  const { getFlowPayload } = useSalesConversationFlowPayload();
  const { initFlow } = useFlowsInitNew();

  const louiseDispatch = useLouiseDispatch();
  const location = useLocation();
  const history = useHistory();
  const uuid = useUuid();
  const campaignOutputUrl = useCampaignsOutputUrl();
  const isRiskAnalysisFlow = useIsRiskAnalysisFlow(
    campaign?.template?.flow_id || '',
  );
  const startRiskAnalysisFlow = useStartRiskAnalysisFlow();

  const insuranceTypeQuery = parse(location.search).insurance_type;

  const insuranceTypes = (
    insuranceTypeQuery && Array.isArray(insuranceTypeQuery)
      ? insuranceTypeQuery
      : [insuranceTypeQuery]
  ).filter((type) => !!type) as string[];

  const insurances = (
    insuranceTypes.length > 0
      ? insuranceTypes.map((insurance) => getProFlowRouteNaming(insurance))
      : uniq(
          campaign?.insurances?.map(({ insurance_type }) =>
            insurance_type?.toLowerCase(),
          ),
        )
  )?.map((insurance: string) =>
    insurance === 'residence' ? 'home' : insurance,
  );

  const retrieveSelectionData = () => {
    if (insurances?.length < 2) {
      louiseDispatch(pushToSelectedRiskObjects(insurances));
    }
    const companies = uniq(
      campaign?.insurances.map(({ insurance_company }) =>
        insurance_company?.toLowerCase(),
      ),
    );
    louiseDispatch(pushToSelectedCompanies(companies));
  };

  const finalize = async () => {
    if (nextUrlCampaign && isSocialLoginUrl)
      return history.push({
        pathname: nextUrlCampaign,
        search: location.search,
      });

    if (prefilledSessionId) {
      const [flowsSessionObject, status] = await getFlowSessionById(
        prefilledSessionId,
      );

      if (status >= 400) {
        if (isSocialLoginUrl) {
          if (isDeMotoVerzekeringEnabled) {
            louiseDispatch(persistSelectionState(prefilledSessionId));
            return startDeMotoVerzekeringFlow();
          }
          if (isVillasureCampaign) {
            return startVillasureOfferFlow(prefilledSessionId);
          }
          if (isRiskAnalysisFlow) {
            return startRiskAnalysisFlow();
          }
          retrieveSelectionData();
          if (insurances?.length < 2) {
            await louiseDispatch(persistSelectionState(prefilledSessionId));
            const sessionId = await initFlow({
              flowId: flowIdSalesConversation,
              inputUrl: location.pathname,
              outputUrl: `${campaignOutputUrl}`,
              payload: await getFlowPayload(prefilledSessionId),
            });
            if (sessionId)
              await louiseDispatch(persistSelectionState(sessionId));
            return;
          }

          return history.push({
            pathname: nextUrl,
            search: stringify({ risk_object: insurances }),
          });
        }

        await louiseDispatch(persistSelectionState(prefilledSessionId));
        const sessionId = await initFlow({
          flowId: flowIdSalesConversation,
          inputUrl: location.pathname,
          outputUrl: `/product/session/finalize`,
          payload: await getFlowPayload(prefilledSessionId),
        });
        if (sessionId) await louiseDispatch(persistSelectionState(sessionId));
        return;
      }

      louiseDispatch(
        pushToSelectedRiskObjects(flowsSessionObject?.insurance_types || []),
      );

      louiseDispatch(
        pushToSelectedCompanies(flowsSessionObject?.companies || []),
      );

      ['car', 'home', 'family', 'legal'].forEach((insurance_type) => {
        louiseDispatch(
          pushToSelectedGuarantees({
            items: flowsSessionObject?.[`${insurance_type}_tags`] || [],
            riskObject: insurance_type,
          }),
        );
      });

      await louiseDispatch(persistSelectionState(prefilledSessionId));

      if (isDeMotoVerzekeringEnabled) {
        return startDeMotoVerzekeringFlow(prefilledSessionId);
      }

      if (isVillasureCampaign) {
        return startVillasureOfferFlow(prefilledSessionId);
      }

      const sessionId = await initFlow({
        flowId: flowIdSalesConversation,
        inputUrl: location.pathname,
        outputUrl: `/product/session/finalize`,
        payload: await getFlowPayload(prefilledSessionId),
      });
      if (sessionId) await louiseDispatch(persistSelectionState(sessionId));
      return;
    } else if (isSocialLoginUrl) {
      retrieveSelectionData();

      if (isDeMotoVerzekeringEnabled) {
        louiseDispatch(persistSelectionState(uuid));
        return startDeMotoVerzekeringFlow(uuid);
      }

      if (isRiskAnalysisFlow) {
        return startRiskAnalysisFlow(prefilledSessionId || uuid);
      }

      if (isVillasureCampaign) {
        return startVillasureOfferFlow(uuid);
      }

      if (insurances?.length < 2) {
        await louiseDispatch(persistSelectionState(uuid));
        const sessionId = await initFlow({
          flowId: flowIdSalesConversation,
          inputUrl: location.pathname,
          outputUrl: `${campaignOutputUrl}`,
          payload: await getFlowPayload(uuid),
        });
        if (sessionId) await louiseDispatch(persistSelectionState(sessionId));
        return;
      }

      const queryObject = parse(location.search);

      return history.push({
        pathname: nextUrl,
        search: stringify({ ...queryObject, risk_object: insurances }),
      });
    }
    return history.push({ pathname: nextUrl, search: location.search });
  };

  const { executeCaptcha, isAuthenticatingByCaptcha, isCaptchaAvailable } =
    useCaptchaV3(finalize);

  const handleOnContinue = () => {
    if (isSocialLoginUrl) {
      executeCaptcha(distribution?.email);
    } else {
      finalize();
    }
  };

  return (
    <ViewWrapper data-test-id="ProdFac_IntroView">
      <IntroContainer
        isLoadingCaptcha={!isCaptchaAvailable || isAuthenticatingByCaptcha}
        displayName={campaign?.display_name || distribution?.name}
        isSocial={!isObjectEmpty(campaign)}
        buttonAction={handleOnContinue}
        campaignStartDate={campaign?.start_date}
        campaignEndDate={campaign?.end_date}
        isCampaignValid={campaign?.code !== 404}
      />
    </ViewWrapper>
  );
};

const ViewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 100vw;
  min-height: 100%;
  overflow-y: scroll;
  background-color: ${({ theme }) =>
    theme['isImmo'] ? '#f4f4f4' : theme.secondary[800]};

  &::-webkit-scrollbar {
    display: none;
  }

  ${mq.mobileL} {
    justify-content: center;
    align-items: center;
  }
`;

export default IntroView;
